import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useLocale } from '@/contexts/LocaleContext';
import { useMarketingTemplates } from '@/hooks/useMarketingTemplates';
import { SITE_SEARCH_ENTRIES, type SearchGroup } from '@/config/siteSearchIndex';
import { CONTENT_PACKS, packUrlId } from '@/data/contentPacks';

/**
 * Site search. Opens from the magnifier in the brand bar or with Cmd/Ctrl-K.
 *
 * Three sources, in the order a visitor cares about them:
 *   1. programs and packs   — what we sell, and the argument pages
 *   2. frameworks           — the live template library, so "DORA" or "CMMC"
 *                             finds the actual published program, not a page
 *                             that mentions it
 *   3. pages                — everything the navigation can already reach
 *
 * The framework results come from the same query the rest of the marketing
 * site uses, so a template published today is searchable today and nothing has
 * to be listed twice. When that query has not resolved (or is blocked, as it is
 * in the sandbox) the pages and packs still search, which is why the list is
 * assembled per-source rather than gated on one fetch.
 *
 * Matching is cmdk's, over the label plus a `terms` string carrying the
 * acronyms and regulation names a person actually types. Without those, a
 * search for "DSAR" or "TPRM" returns nothing, which is worse than no search.
 */
const GROUP_ORDER: SearchGroup[] = ['platform', 'solutions', 'resources', 'company', 'legal'];

/** Separates the label from the path inside an item's cmdk value. */
const SEP = '\u241F';

/**
 * Substring scoring, not cmdk's default fuzzy match.
 *
 * The default scores a subsequence, so with keyword strings attached "dsar"
 * matched "Public Sector / DefenSe... A... R..." and every pack outranked the
 * page actually called Privacy Request. Ranking here is: the label starts with
 * what you typed, then the label contains it, then a keyword contains it. A
 * multi-word query has to place every word somewhere. Anything else scores 0
 * and disappears, which is the behaviour a search box is expected to have.
 */
const score = (value: string, search: string, keywords?: string[]): number => {
  const q = search.trim().toLowerCase();
  if (!q) return 1;
  const label = value.split(SEP)[0].toLowerCase();
  const kw = (keywords ?? []).join(' ').toLowerCase();
  if (label.startsWith(q)) return 1;
  if (label.includes(q)) return 0.8;
  if (kw.includes(q)) return 0.4;
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length > 1 && words.every((w) => label.includes(w) || kw.includes(w))) return 0.3;
  return 0;
};

const SiteSearch = ({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocale();
  const { data: blueprints = [] } = useMarketingTemplates();

  const go = useCallback(
    (to: string) => {
      onOpenChange(false);
      navigate(getLocalizedPath(to));
    },
    [navigate, getLocalizedPath, onOpenChange],
  );

  const programs = useMemo(
    () =>
      CONTENT_PACKS.filter((p) => p.tileRegulations?.length).map((p) => ({
        to: p.program ? `/programs/${packUrlId(p)}` : `/templates?pack=${packUrlId(p)}`,
        label: p.name,
        terms: [
          p.description,
          ...(p.tileRegulations ?? []).map((r) => r.label),
          ...p.jurisdictions,
        ].join(' '),
      })),
    [],
  );

  const frameworks = useMemo(
    () =>
      blueprints
        .filter((b) => b.dbSlug)
        .slice(0, 200)
        .map((b) => ({
          to: `/templates?q=${encodeURIComponent(b.name as string)}`,
          label: b.name as string,
          controls: (b as { controls?: number }).controls,
        })),
    [blueprints],
  );

  const pages = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        items: SITE_SEARCH_ENTRIES.filter((e) => e.group === group).map((e) => ({
          to: e.to,
          label: t(e.key, e.fallback),
          terms: e.terms ?? '',
        })),
      })),
    [t],
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} filter={score}>
      <CommandInput placeholder={t('search.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('search.empty')}</CommandEmpty>

        <CommandGroup heading={t('search.groupPrograms')}>
          {programs.map((p) => (
            <CommandItem
              key={p.to}
              value={`${p.label}${SEP}${p.to}`}
              keywords={[p.terms]}
              onSelect={() => go(p.to)}
            >
              {p.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {frameworks.length > 0 && (
          <CommandGroup heading={t('search.groupFrameworks')}>
            {frameworks.map((f) => (
              <CommandItem key={f.to} value={`${f.label}${SEP}${f.to}`} onSelect={() => go(f.to)}>
                <span className="flex-1">{f.label}</span>
                {typeof f.controls === 'number' && f.controls > 0 && (
                  <span className="ml-3 shrink-0 font-mono text-xs text-muted-foreground">
                    {t('search.controlCount', { count: f.controls })}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {pages.map(({ group, items }) => (
          <CommandGroup key={group} heading={t(`search.group_${group}`, GROUP_FALLBACK[group])}>
            {items.map((item) => (
              <CommandItem
                key={item.to}
                value={`${item.label}${SEP}${item.to}`}
                keywords={[item.terms]}
                onSelect={() => go(item.to)}
              >
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

const GROUP_FALLBACK: Record<SearchGroup, string> = {
  platform: 'Platform',
  solutions: 'Solutions',
  resources: 'Resources',
  company: 'Company',
  legal: 'Trust and legal',
};

/**
 * The trigger. Lives in the brand bar beside the other utility items, so search
 * sits with sign-in and language rather than competing with the primary nav.
 */
export const SiteSearchTrigger = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key?.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        /* min-h-6 / min-w-6 keeps the hit target at the WCAG 2.2 SC 2.5.8
           minimum of 24px even below lg, where the label is hidden and only
           the 12px icon renders. */
        className="inline-flex items-center justify-center min-h-6 min-w-6 gap-1 font-medium text-white/55 transition-colors hover:text-white/90"
        aria-label={t('search.open')}
      >
        <Search className="h-3 w-3" aria-hidden />
        <span className="hidden lg:inline">{t('search.label')}</span>
      </button>
      <SiteSearch open={open} onOpenChange={setOpen} />
    </>
  );
};

export default SiteSearch;
